import {Dialog, DialogBackdrop, DialogPanel, DialogTitle, Switch} from "@headlessui/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {DateTimePickerInput} from "../../Form/DateTimePickerInput";
import {TextAreaInput} from "../../Form/TextAreaInput";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import * as React from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useBoundStore} from "../../../Store/store";
import {NumberInput} from "../../Form/NumberInput";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {ControlledSwitch} from "../../Form/ControlledSwitch";
import {SelectInput} from "../../Form/SelectInput";
import DatePicker from "react-datepicker";
import {useEffect, useState} from "react";
import nl from 'date-fns/locale/nl';

export const CreateShiftDialog = (props) => {
    const {
        open,
        onClose,
        onCreated,
        customerId,
        branches,
    } = props;

    const {t} = useTranslation();

    const createShift = useBoundStore((state) => state.createShift);
    const branchesFromStore = useBoundStore((state) => state.branchesNotPaginated);
    const getMyBranchesNotPaginated = useBoundStore((state) => state.getMyBranchesNotPaginated);
    const branchesNotPaginated = branches ?? branchesFromStore;

    const [repeatDates, setRepeatDates] = useState([]);
    const [isCreating, setIsCreating] = useState(false);

    const shiftApplicationPossibilities = [
        {id: 1, value: 'both', label: t('shifts.both')},
        {id: 2, value: 'only_verpleegkundige', label: t('shifts.filter_only_nurse')},
        {id: 3, value: 'only_zorgkundige', label: t('shifts.filter_only_caregiver')},
    ];

    const statutenOptions = [
        {id: 1, value: 'freelance', label: t('statuten.freelance')},
        {id: 2, value: 'interim', label: t('statuten.interim')},
        {id: 3, value: 'student', label: t('statuten.student')},
        {id: 4, value: 'flexi_job', label: t('statuten.flexi_job')},
    ];

    useEffect(() => {
        if (branches || !open) return;
        getMyBranchesNotPaginated();
    }, [open, branches, getMyBranchesNotPaginated]);

    useEffect(() => {
        reset({
            branch_id: branchesNotPaginated.length === 1 ? branchesNotPaginated[0].id : null,
            start_time: null,
            end_time: null,
            break_duration: null,
            is_published: false,
            remarks: null,
            shift_repeats_enabled: false,
            shift_repeats: [],
            shift_applications_possibility: 'both',
            allowed_statuten: ['freelance'],
        })

        setRepeatDates([]);
        setIsCreating(false);
    }, [open])

    const schema = yup.object({
        start_time: yup.string().required(),
        end_time: yup
         .string()
         .required()
         .test(
           "is-after-start",
           "De eindtijd moet na de starttijd zijn",
           function (value) {
             const { start_time } = this.parent;
             if (!start_time || !value) return true;
             return dayjs(value).isAfter(dayjs(start_time));
           }
         ),
        break_duration: yup.string().nullable(),
        is_published: yup.boolean(),
        remarks: yup.string().nullable(),
        shift_repeats_enabled: yup.boolean().nullable(),
        shift_applications_possibility: yup.mixed().nullable(),
        shift_repeats: yup.array().nullable(),
        allowed_statuten: yup.array().of(yup.string()).min(1, t('shifts_module.allowed_statuten_min')).required(),
        branch_id: yup.string().required(),
    }).required()

    const {
        register,
        handleSubmit,
        formState: {errors},
        getValues,
        reset,
        setValue,
        control,
        watch,
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            branch_id: null,
            start_time: null,
            end_time: null,
            break_duration: null,
            is_published: false,
            remarks: null,
            shift_repeats_enabled: false,
            shift_repeats: [],
            shift_applications_possibility: 'both',
            allowed_statuten: ['freelance'],
        }
    });

    const onSubmit = async (data) => {
        const transformedData = {
            ...data,
            start_time: dayjs(data.start_time).format('YYYY-MM-DD HH:mm:ss'),
            end_time: dayjs(data.end_time).format('YYYY-MM-DD HH:mm:ss'),
            is_published: data.is_published === "" ? false : data.is_published,
            shift_applications_possibility: data.shift_applications_possibility ?? 'both',
            shift_repeats: repeatDates.map(date => dayjs(date).format('YYYY-MM-DD')) ?? [],
        }

        if(!data?.shift_repeats_enabled){
            delete transformedData.shift_repeats;
        }

        setIsCreating(true);
        try {
            await createShift(transformedData);
            toast.success(t('shifts.toast_created'));
            if (onCreated) {
                await onCreated();
            }
            onClose();
        } catch (error) {
            console.error('Error creating shift', error);
            setIsCreating(false);
        }
    }

    const watchedRepeatShifts = watch('shift_repeats_enabled');

    return (
        <Dialog className="relative z-50" open={open} onClose={onClose}>
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
            />

            <div className="fixed inset-0 overflow-y-scroll">
                <div className="absolute inset-0 overflow-y-scroll">
                    <div className="pointer-events-none p-4 rounded-lg fixed inset-y-0 right-0 flex max-w-full">
                        <DialogPanel
                            transition
                            className="pointer-events-auto w-screen max-w-lg transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                        >
                            <div className="flex h-full rounded-2xl flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                                <div className="px-4 sm:px-6">
                                    <div className="flex items-start justify-between">
                                        <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                                            {t('shifts.dialog_create_shift_title')}
                                        </DialogTitle>
                                        <div className="ml-3 flex h-7 items-center">
                                            <button
                                                type="button"
                                                className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                                onClick={() => onClose()}
                                            >
                                                <span className="absolute -inset-2.5" />
                                                <span className="sr-only">Close panel</span>
                                                <FontAwesomeIcon icon={faTimes} className="h-6 w-6" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="relative mt-6 flex-1 px-4 sm:px-6">
                                    <form className="w-full" onSubmit={handleSubmit(onSubmit)}>

                                        <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 md:space-x-4'}>
                                            <DateTimePickerInput
                                                label={'Start time'}
                                                name={'start_time'}
                                                register={register}
                                                setValue={setValue}
                                                translationIndex={'general.fields.start_time'}
                                                errors={errors}
                                            />
                                            <DateTimePickerInput
                                                label={'End time'}
                                                name={'end_time'}
                                                register={register}
                                                setValue={setValue}
                                                translationIndex={'general.fields.end_time'}
                                                errors={errors}
                                            />
                                        </div>

                                        {
                                            branchesNotPaginated.length > 1 && (
                                                <div className="mb-2">
                                                    <SelectInput
                                                        name={'branch_id'}
                                                        options={branchesNotPaginated.map((branch) => {
                                                            return {
                                                                id: branch.id,
                                                                value: branch.id,
                                                                label: branch.company_name
                                                            }
                                                        })}
                                                        setValue={setValue}
                                                        control={control}
                                                        value={getValues('branch_id') ?? null}
                                                        placeholder={t('shifts.select_branch')}
                                                        translationIndex={t('shifts.branches')}
                                                        errors={errors}
                                                    />
                                                </div>
                                            )
                                        }

                                        <div className="mb-2">
                                            <SelectInput
                                                name={'shift_applications_possibility'}
                                                options={shiftApplicationPossibilities}
                                                setValue={setValue}
                                                control={control}
                                                value={getValues('shift_applications_possibility') ?? null}
                                                placeholder={t('general.fields.shift_applications_possibility')}
                                                translationIndex={'shifts.shift_applications_possibility'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <SelectInput
                                                name={'allowed_statuten'}
                                                options={statutenOptions}
                                                multiple={true}
                                                setValue={setValue}
                                                control={control}
                                                currentValue={getValues('allowed_statuten') ?? ['freelance']}
                                                translationIndex={'shifts.allowed_statuten'}
                                                helperText={t('shifts.allowed_statuten_helper')}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <NumberInput
                                                register={register}
                                                name={'break_duration'}
                                                translationIndex={'general.fields.break_duration'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextAreaInput
                                                register={register}
                                                name={'remarks'}
                                                translationIndex={'general.fields.remarks'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <div className="flex flex-row justify-start">
                                                <ControlledSwitch
                                                    control={control}
                                                    name={'is_published'}
                                                    getValues={getValues}
                                                    setValue={setValue}
                                                    errors={errors}
                                                    translationIndex={'general.fields.is_published'}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2">
                                            <div className="flex flex-row justify-start">

                                                <ControlledSwitch
                                                    control={control}
                                                    name={'shift_repeats_enabled'}
                                                    getValues={getValues}
                                                    errors={errors}
                                                    setValue={setValue}
                                                    translationIndex={'shifts.dialog_repeat_shifts'}
                                                />

                                            </div>
                                            {
                                                watchedRepeatShifts && (
                                                    <div className="mt-4">
                                                        <DatePicker
                                                            selectedDates={repeatDates}
                                                            className={'w-full mt-4'}
                                                            inline={true}
                                                            locale={nl}
                                                            selectsMultiple={true}
                                                            shouldCloseOnSelect={false}
                                                            disabledKeyboardNavigation={true}
                                                            onChange={(dates) => {
                                                                setRepeatDates(dates);
                                                            }}
                                                            minDate={new Date()}
                                                        />

                                                    </div>
                                                )
                                            }
                                        </div>
                                        <PrimaryButton
                                            onClick={handleSubmit(onSubmit)}
                                            disabled={isCreating}
                                        >
                                            {isCreating ? (
                                                <span className="flex items-center justify-center">
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Bezig met aanmaken...
                                                </span>
                                            ) : (
                                                <>{t('general.actions.save')} Shift</>
                                            )}
                                        </PrimaryButton>
                                    </form>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}